import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { ILoanQuote, IBorrower, ILoan, IQuotationFormOptions, IGlobalComparatorOptions } from '@/app/models/loanQuote'
import { ILoanOffer, INotApplicableOffer } from '@/app/endpoints/loanComparatorEndpoints'
import { CURRENCIES } from '@/app/schemas/loan-comparator/loan.schema'
import { LOAN_INFORMATION_TYPES } from '@/app/constants/loanTypes'

// Re-export types
export type LoanQuote = ILoanQuote
export type Borrower = IBorrower
export type Loan = ILoan

// Constants
export const LOAN_YEAR_DISPLAY_TOTAL = -1
export const AVERAGE_LOAN_DURATION = 8
export const VIP_AMOUNT_THRESHOLD = 30000

const UTWIN_API_NAMES = [
    'prevoirutwin',
    'malakoffhumanis',
    'mncap',
    'suravenir',
    'swisslife',
    'cnp_access_protect',
]

export interface UIState {
    currentStep: number
    loading: boolean
    errors: Record<string, string>
}

// Hydration state - track if store has been rehydrated from localStorage
interface HydrationState {
    _hasHydrated: boolean
    _isServerDataLoaded: boolean
    setHasHydrated: (state: boolean) => void
    setServerDataLoaded: (state: boolean) => void
}

// Borrower slice
interface BorrowerSlice {
    borrower: {
        updateMain: (updates: Partial<IBorrower>) => void
        updateCo: (updates: Partial<IBorrower>) => void
        addCoBorrower: () => void
        deactivateCoBorrower: () => void
        removeCoBorrower: () => void
    }
}

// Loan slice
interface LoanSlice {
    loan: {
        add: () => void
        update: (id: number, updates: Partial<ILoan>) => void
        remove: (id: number) => void
    }
}

// Quote slice
interface QuoteSlice {
    quote: LoanQuote | null
    quoteActions: {
        setQuote: (quote: LoanQuote | null) => void
        update: (updates: Partial<LoanQuote>) => void
        reset: () => void
    }
}

// UI slice
interface UISlice {
    ui: UIState
    uiActions: {
        setCurrentStep: (step: number) => void
        setLoading: (loading: boolean) => void
        setError: (field: string, error: string) => void
        clearError: (field: string) => void
        clearAllErrors: () => void
    }
}

// Form Options slice
interface FormOptionsSlice {
    formOptions: IQuotationFormOptions | null
    formOptionsActions: {
        setFormOptions: (options: IQuotationFormOptions) => void
    }
}

// Global Options slice
interface GlobalOptionsSlice {
    globalOptions: IGlobalComparatorOptions | null
    globalOptionsActions: {
        setGlobalOptions: (options: IGlobalComparatorOptions) => void
    }
}

// Offers slice
interface OffersSlice {
    offers: ILoanOffer[]
    notApplicableOffers: INotApplicableOffer[]
    offersLoading: boolean
    chosenOffer: ILoanOffer | null
    yearAmountToDisplayPrice: number
    offersActions: {
        setOffers: (offers: ILoanOffer[]) => void
        setNotApplicableOffers: (offers: INotApplicableOffer[]) => void
        setOffersLoading: (loading: boolean) => void
        setChosenOffer: (offer: ILoanOffer | null) => void
        setYearAmountToDisplayPrice: (year: number) => void
    }
}

// Subscription preferences slice (set from identity gateway on results page)
interface SubscriptionPrefsSlice {
    whatsappOptinFromIdentity: boolean
    genderFromIdentity: number | null
    promoCodeRefusedAtIdentity: string | null
    subscriptionPrefsActions: {
        setWhatsappOptinFromIdentity: (value: boolean) => void
        setGenderFromIdentity: (value: number | null) => void
        setPromoCodeRefusedAtIdentity: (code: string | null) => void
    }
}

// Fields the guide chatbot filled in, as `collected` names plus `<name>:estimate` for ranges to confirm
interface PrefillSlice {
    prefilledByChat: string[]
    prefillActions: {
        setPrefilledByChat: (fields: string[]) => void
    }
}

// Combined state
type LoanComparatorState = QuoteSlice &
    BorrowerSlice &
    LoanSlice &
    UISlice &
    FormOptionsSlice &
    GlobalOptionsSlice &
    OffersSlice &
    SubscriptionPrefsSlice &
    PrefillSlice &
    HydrationState

// Default factory for creating a new borrower
const createDefaultBorrower = (): Partial<IBorrower> => ({
    smoker: false,
    medical_risk: false,
    has_risky_job: false,
    is_expat: false,
    handwork: false,
    clerk_job: false,
    height: false,
    looking_for_work: false,
    proportion: 100,
    distance: 0,
    spe_type_work: 0,
    activated: true,
    rejected_by_insurance: false,
    side_loans: false,
    options: [],
    real_options: [],
    workflows: [],
    prospect_info: {
        id: 0,
        created_at: new Date().toISOString(),
        last_name_and_first_name: '',
        first_name_and_last_name: '',
        prospect: {
            id: 0,
            created_at: new Date().toISOString(),
            email_preference: 0,
            email_preference_token: '',
            received_recall_email: false,
            received_car_recall_email: false,
            received_health_covid_email: false,
        },
    },
})

// Counter for generating unique temporary IDs for new loans (negative to distinguish from server IDs)
let tempLoanIdCounter = -1

// Default factory for creating a new loan with unique temporary ID
const createDefaultLoan = (): Partial<ILoan> => {
    const id = tempLoanIdCounter
    tempLoanIdCounter -= 1
    return {
        id,
        sum: 0,
        period: 0,
        time_offset: 0,
        month_left: 0,
        sum_left: 0,
        partial_time_offset: false,
        palier: [],
        information_type: LOAN_INFORMATION_TYPES.SUM_INITIAL,
        selected_currency: CURRENCIES.EUR,
        created_at: new Date().toISOString(),
        next_anniversary_date: new Date().toISOString(),
    }
}

// Initial state - null quote (will be set from API or created fresh)
const initialQuote: LoanQuote | null = null

const initialUIState: UIState = {
    currentStep: 1,
    loading: false,
    errors: {},
}

export const useLoanComparatorStore = create<LoanComparatorState>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    quote: initialQuote,
                    ui: initialUIState,
                    formOptions: null,
                    globalOptions: null,
                    offers: [],
                    notApplicableOffers: [],
                    offersLoading: false,
                    chosenOffer: null,
                    yearAmountToDisplayPrice: LOAN_YEAR_DISPLAY_TOTAL,

                    // Subscription preferences
                    whatsappOptinFromIdentity: false,
                    genderFromIdentity: null,
                    promoCodeRefusedAtIdentity: null,
                    subscriptionPrefsActions: {
                        setWhatsappOptinFromIdentity: (value) =>
                            set({ whatsappOptinFromIdentity: value }, false, 'subscriptionPrefs/setWhatsappOptinFromIdentity'),
                        setGenderFromIdentity: (value) =>
                            set({ genderFromIdentity: value }, false, 'subscriptionPrefs/setGenderFromIdentity'),
                        setPromoCodeRefusedAtIdentity: (code) =>
                            set({ promoCodeRefusedAtIdentity: code }, false, 'subscriptionPrefs/setPromoCodeRefusedAtIdentity'),
                    },

                    prefilledByChat: [],
                    prefillActions: {
                        setPrefilledByChat: (fields) =>
                            set({ prefilledByChat: fields }, false, 'prefill/setPrefilledByChat'),
                    },

                    // Hydration state
                    _hasHydrated: false,
                    _isServerDataLoaded: false,
                    setHasHydrated: (state: boolean) => {
                        set({ _hasHydrated: state }, false, 'hydration/setHasHydrated')
                    },
                    setServerDataLoaded: (state: boolean) => {
                        set({ _isServerDataLoaded: state }, false, 'hydration/setServerDataLoaded')
                    },

                    // Form Options actions
                    formOptionsActions: {
                        setFormOptions: (options) =>
                            set(
                                {
                                    formOptions: options,
                                },
                                false,
                                'formOptions/setFormOptions'
                            ),
                    },

                    // Global Options actions
                    globalOptionsActions: {
                        setGlobalOptions: (options) =>
                            set(
                                {
                                    globalOptions: options,
                                },
                                false,
                                'globalOptions/setGlobalOptions'
                            ),
                    },

                    // Offers actions
                    offersActions: {
                        setOffers: (offers) =>
                            set({ offers }, false, 'offers/setOffers'),
                        setNotApplicableOffers: (offers) =>
                            set({ notApplicableOffers: offers }, false, 'offers/setNotApplicableOffers'),
                        setOffersLoading: (loading) =>
                            set({ offersLoading: loading }, false, 'offers/setOffersLoading'),
                        setChosenOffer: (offer) =>
                            set({ chosenOffer: offer }, false, 'offers/setChosenOffer'),
                        setYearAmountToDisplayPrice: (year) =>
                            set({ yearAmountToDisplayPrice: year }, false, 'offers/setYearAmountToDisplayPrice'),
                    },

                    // Borrower actions - using Immer for simpler updates
                    borrower: {
                        updateMain: (updates) =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    Object.assign(state.quote.main_borrower, updates)
                                    if (updates.prospect_info) {
                                        Object.assign(state.quote.main_borrower.prospect_info, updates.prospect_info)
                                    }
                                },
                                false,
                                'borrower/updateMain'
                            ),

                        updateCo: (updates) =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    if (!state.quote.co_borrower) {
                                        state.quote.co_borrower = createDefaultBorrower() as IBorrower
                                    }
                                    Object.assign(state.quote.co_borrower, updates)
                                    if (updates.prospect_info) {
                                        Object.assign(state.quote.co_borrower.prospect_info, updates.prospect_info)
                                    }
                                },
                                false,
                                'borrower/updateCo'
                            ),

                        addCoBorrower: () =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    if (state.quote.co_borrower?.activated === false) {
                                        // Réactivation douce : préserve les données saisies lors d'un aller-retour Seul/À deux
                                        state.quote.co_borrower.activated = true
                                    } else if (!state.quote.co_borrower?.activated) {
                                        // Absent ou stub serveur jamais renseigné (activated null) : défauts client
                                        state.quote.co_borrower = createDefaultBorrower() as IBorrower
                                    }
                                },
                                false,
                                'borrower/addCoBorrower'
                            ),

                        deactivateCoBorrower: () =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    if (!state.quote.co_borrower) {
                                        // Stub désactivé : « Seul » doit être représentable même sans co existant
                                        state.quote.co_borrower = {
                                            ...createDefaultBorrower(),
                                            activated: false,
                                        } as IBorrower
                                    } else {
                                        state.quote.co_borrower.activated = false
                                    }
                                },
                                false,
                                'borrower/deactivateCoBorrower'
                            ),

                        removeCoBorrower: () =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    state.quote.co_borrower = undefined
                                },
                                false,
                                'borrower/removeCoBorrower'
                            ),
                    },

                    // Loan actions - using Immer for simpler updates
                    loan: {
                        add: () =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    state.quote.loans.push(createDefaultLoan() as ILoan)
                                },
                                false,
                                'loan/add'
                            ),

                        update: (id, updates) =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    const loan = state.quote.loans.find((l) => l.id === id)
                                    if (loan) {
                                        Object.assign(loan, updates)
                                    }
                                },
                                false,
                                'loan/update'
                            ),

                        remove: (id) =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    const index = state.quote.loans.findIndex((l) => l.id === id)
                                    if (index !== -1) {
                                        state.quote.loans.splice(index, 1)
                                    }
                                },
                                false,
                                'loan/remove'
                            ),
                    },

                    // Quote actions
                    quoteActions: {
                        setQuote: (quote) =>
                            set(
                                {
                                    quote,
                                },
                                false,
                                'quote/setQuote'
                            ),

                        update: (updates) =>
                            set(
                                (state) => {
                                    if (!state.quote) return
                                    Object.assign(state.quote, updates)
                                },
                                false,
                                'quote/update'
                            ),

                        reset: () =>
                            set(
                                {
                                    quote: initialQuote,
                                    ui: initialUIState,
                                },
                                false,
                                'quote/reset'
                            ),
                    },

                    // UI actions - using Immer for simpler updates
                    uiActions: {
                        setCurrentStep: (step) =>
                            set(
                                (state) => {
                                    state.ui.currentStep = step
                                },
                                false,
                                'ui/setCurrentStep'
                            ),

                        setLoading: (loading) =>
                            set(
                                (state) => {
                                    state.ui.loading = loading
                                },
                                false,
                                'ui/setLoading'
                            ),

                        setError: (field, error) =>
                            set(
                                (state) => {
                                    state.ui.errors[field] = error
                                },
                                false,
                                'ui/setError'
                            ),

                        clearError: (field) =>
                            set(
                                (state) => {
                                    delete state.ui.errors[field]
                                },
                                false,
                                'ui/clearError'
                            ),

                        clearAllErrors: () =>
                            set(
                                (state) => {
                                    state.ui.errors = {}
                                },
                                false,
                                'ui/clearAllErrors'
                            ),
                    },
                }),
                {
                    name: 'loan-comparator-store',
                }
            )
        ),
        {
            name: 'loan-comparator-storage',
            // Only persist the quote data, not UI state
            partialize: (state) => ({ quote: state.quote, prefilledByChat: state.prefilledByChat }),
            // Called when rehydration is complete
            onRehydrateStorage: () => (state) => {
                // Sync tempLoanIdCounter to avoid ID collisions with rehydrated loans
                if (state?.quote?.loans?.length) {
                    const minId = state.quote.loans.reduce((min, loan) => Math.min(min, loan.id), 0)
                    tempLoanIdCounter = minId - 1
                }
                state?.setHasHydrated(true)
            },
        }
    )
)

// Hook to wait for hydration - useful for SSR/client hydration sync
export const useHydration = () => {
    return useLoanComparatorStore((state) => state._hasHydrated)
}

// Hook to check if server data has been loaded into the store
export const useServerDataLoaded = () => {
    return useLoanComparatorStore((state) => state._isServerDataLoaded)
}

// --- Derived state helpers (pure functions, not in the store) ---

export const hasRiskyJob = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    return !!(quote.main_borrower.job_risk || quote.co_borrower?.job_risk)
}

export const hasMedicalRisk = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    return !!(quote.main_borrower.medical_risk || quote.co_borrower?.medical_risk)
}

export const hasExpat = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    return !!(quote.main_borrower.is_expat || quote.co_borrower?.is_expat)
}

export const needsSwitching = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    return ['switching', 'bourquin'].includes(quote.type_quote)
}

export const isAtLeastOneLemoine = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    // Same guard as isAllLemoine: a deactivated co-borrower keeps its answers and must not make the quote eligible
    return !!(
        quote.main_borrower.outstanding_total_credit_under_lemoine_limit ||
        (quote.co_borrower?.activated && quote.co_borrower.outstanding_total_credit_under_lemoine_limit)
    )
}

export const isAllLemoine = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    const mainLemoine = !!quote.main_borrower.outstanding_total_credit_under_lemoine_limit
    // Garder sur activated : l'objet co_borrower existe toujours (inactivé, flag null), il ne doit
    // pas invalider l'éligibilité quand il n'y a pas de co-emprunteur (cf. Symfony with_co_borrower).
    if (quote.co_borrower?.activated) {
        return mainLemoine && !!quote.co_borrower.outstanding_total_credit_under_lemoine_limit
    }
    return mainLemoine
}

export const isUtwinAPI = (apiName: string): boolean => {
    return UTWIN_API_NAMES.includes(apiName)
}

export const loanDurationLeftInYears = (quote: ILoanQuote | null): number => {
    if (!quote || !quote.loans.length) return 0
    const maxMonths = Math.max(...quote.loans.map((l) => l.month_left || l.period || 0))
    return Math.ceil(maxMonths / 12)
}

export const borrowerIdentity = (quote: ILoanQuote | null) => {
    if (!quote) return { firstName: '', lastName: '', email: '', phone: '' }
    const pi = quote.main_borrower.prospect_info
    return {
        firstName: pi?.first_name || '',
        lastName: pi?.last_name || '',
        email: pi?.prospect?.email || '',
        phone: pi?.phone || '',
    }
}

export const isBPSIS = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    return quote.chosen_company?.broker?.toLowerCase() === 'bpsis'
}

export const isNewLoan = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    return quote.type_quote === 'newLoan'
}

export const isExternalDelegation = (quote: ILoanQuote | null): boolean => {
    if (!quote) return false
    return !!quote.delegation
}
